import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Save, X, Book, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const PREVIEW_CSS = `
.preview-reader .body-text { color: #f0ddb0; line-height: 2; margin-bottom: 1em; font-size: 0.9rem; }
.preview-reader .body-text.text-center { text-align: center; }
.preview-reader .section-heading { color: #fcd34d; font-weight: 700; font-size: 1.1rem; text-align: center; letter-spacing: 0.08em; margin: 1.5em 0 0.8em; padding: 0.6em 0; border-top: 1px solid rgba(200,165,90,0.2); border-bottom: 1px solid rgba(200,165,90,0.2); }
.preview-reader .glow-text { text-shadow: 0 0 8px currentColor; }
.preview-reader .rich-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; line-height: 1.7; margin: 1.5em 0; }
.preview-reader .rich-table td { vertical-align: top; padding: 10px 14px; border: 1px solid rgba(200,165,90,0.12); }
.preview-reader .rich-table tr:first-child td { font-weight: 600; }
`;

const BookChaptersView = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingChapter, setEditingChapter] = useState(null);
  const [editFormData, setEditFormData] = useState({ title: '', content: '' });
  const [previewChapterId, setPreviewChapterId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchBookAndChapters();
  }, [bookId]);

  const fetchBookAndChapters = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/books/${bookId}`,
        {
          headers: headers,
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBook(data.book);
        setChapters(data.chapters);
      } else {
        console.error('Failed to fetch book and chapters');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error fetching book:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChapter = (chapter) => {
    setEditingChapter(chapter.chapter_id);
    setEditFormData({
      title: chapter.title,
      content: chapter.content
    });
  };

  const handleSaveChapter = async (chapterId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/books/chapters/${chapterId}`,
        {
          method: 'PUT',
          headers: headers,
          credentials: 'include',
          body: JSON.stringify(editFormData)
        }
      );

      if (response.ok) {
        await fetchBookAndChapters();
        setEditingChapter(null);
      } else {
        alert('Failed to update chapter');
      }
    } catch (error) {
      console.error('Error updating chapter:', error);
      alert('Error updating chapter');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Are you sure you want to delete this chapter?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/books/chapters/${chapterId}`,
        {
          method: 'DELETE',
          headers: headers,
          credentials: 'include'
        }
      );

      if (response.ok) {
        await fetchBookAndChapters();
      } else {
        alert('Failed to delete chapter');
      }
    } catch (error) {
      console.error('Error deleting chapter:', error);
      alert('Error deleting chapter');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <p>Book not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-8 border-b">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{book.title}</h1>
              {book.subtitle && (
                <p className="text-gray-300 text-lg mb-2">{book.subtitle}</p>
              )}
              <p className="text-gray-400">by {book.author}</p>
              {book.description && (
                <p className="text-gray-300 mt-4 max-w-3xl">{book.description}</p>
              )}
            </div>
            <Badge className={
              book.status === 'published' ? 'bg-green-500' :
              book.status === 'uploaded' ? 'bg-blue-500' :
              'bg-gray-500'
            }>
              {book.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">
            Chapters ({chapters.length})
          </h2>
          <p className="text-gray-600">
            View and edit the chapters for this book
          </p>
        </div>

        {chapters.length === 0 ? (
          <Card className="p-12 text-center">
            <Book className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Chapters Yet</h3>
            <p className="text-gray-600">
              Upload a .docx file from the admin dashboard to add chapters
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {chapters.map((chapter, index) => (
              <Card key={chapter.chapter_id} className="p-6">
                {editingChapter === chapter.chapter_id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Chapter Title
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded-lg p-3"
                        value={editFormData.title}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, title: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Content
                      </label>
                      <textarea
                        className="w-full border rounded-lg p-3 font-mono text-sm"
                        rows={15}
                        value={editFormData.content}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, content: e.target.value })
                        }
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveChapter(chapter.chapter_id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEditingChapter(null)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">
                          {chapter.chapter_number}. {chapter.title}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Order: {chapter.order} | Created: {new Date(chapter.created_at).toLocaleDateString()}
                          {chapter.rich_content && <span className="ml-2 text-emerald-600 font-medium">Rich Content</span>}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {chapter.rich_content && (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`preview-chapter-${chapter.chapter_id}`}
                            onClick={() => setPreviewChapterId(previewChapterId === chapter.chapter_id ? null : chapter.chapter_id)}
                            className={previewChapterId === chapter.chapter_id ? 'bg-amber-50 border-amber-400' : ''}
                          >
                            {previewChapterId === chapter.chapter_id ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                            {previewChapterId === chapter.chapter_id ? 'Hide Preview' : 'Reader Preview'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditChapter(chapter)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteChapter(chapter.chapter_id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>

                    {previewChapterId === chapter.chapter_id ? (
                      <div className="rounded-xl overflow-hidden border border-stone-700" style={{ background: '#060503' }}>
                        <div className="px-4 py-2 border-b border-stone-700 flex items-center gap-2" style={{ background: '#0a0806' }}>
                          <Eye className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-xs text-amber-500 font-mono tracking-wider uppercase">Reader Preview — This is how it looks to your audience</span>
                        </div>
                        <style>{PREVIEW_CSS}</style>
                        <div className="preview-reader p-6 sm:p-10 max-h-[600px] overflow-y-auto"
                          data-testid={`rich-preview-${chapter.chapter_id}`}
                          dangerouslySetInnerHTML={{ __html: chapter.rich_content }} />
                      </div>
                    ) : (
                      <div className="prose max-w-none">
                        <div className="bg-gray-50 rounded-lg p-4 border">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {chapter.content.substring(0, 500)}
                            {chapter.content.length > 500 && '...'}
                          </p>
                          {chapter.content.length > 500 && (
                            <p className="text-sm text-gray-500 mt-2">
                              ({chapter.content.length} characters total)
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BookChaptersView;
